import {
	devicesFastPollExpiry$,
	userBlockStatusFastPollExpiryMap$,
	usersFastPollExpiry$,
	type UserFastPollExpiryMap,
} from '@op/app-shared'
import {
	activeChildId$,
	checkEmailVerification,
	fetchAccessState,
	fetchBlockStatuses,
	fetchDevices,
	fetchEncryptedPrivate,
	fetchSKUDefs,
	fetchUserBlockStatus,
	fetchUserRules,
	fetchUsers,
	isAuthenticated$,
	setPurchaseSummary,
	shouldSetNotTrialEligible$,
} from '@op/services'
import { memo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { usePolling } from './hooks'
import { useAppDispatch } from './types'
import { accountGenArgs } from './util/crypto'

export const AuthPollingImpl = () => {
	const d = useAppDispatch()
	const blockStatusFastPoll = useSelector(userBlockStatusFastPollExpiryMap$)
	const devicesFastPoll = useSelector(devicesFastPollExpiry$)
	const shouldSetNotTrialEligible = useSelector(shouldSetNotTrialEligible$)
	const userId = useSelector(activeChildId$)
	const usersFastPollExpiry = useSelector(usersFastPollExpiry$)

	/**
	 * NOTE: `Date.now()` here can function because polling causes a re-render. Otherwise, we'd have
	 * to set an interval and set this to a periodically updating `useState`.
	 */
	const now = Date.now()

	const [blockStatusPollingMs, userBlockStatusPollingMs, blockStatusFastPollIds] =
		getBlockStatusPollingData(blockStatusFastPoll, now)
	const devicesPollingMs = now < devicesFastPoll ? usePolling.FAST : usePolling.LONG
	const usersPollingMs = now < usersFastPollExpiry ? usePolling.FAST : usePolling.LONG

	usePolling(
		[
			(extra) => d(fetchEncryptedPrivate({ ...accountGenArgs(), ...extra })),
			(extra) => d(fetchSKUDefs(extra)),
		],
		usePolling.EXTENDED,
		false,
	)

	usePolling([(extra) => d(fetchAccessState(extra))], usePolling.LONG)
	usePolling([() => d(checkEmailVerification())], usePolling.EXTENDED)

	/**
	 * NOTE: BlockStatus and rules are too coupled to separate calls. Although, even like this, we
	 * can be out of sync until both calls are resolved. Should look into using `BlockStatus.rule` as
	 * the source of truth for `ChildStatus`.
	 */
	usePolling(
		[
			(extra) => {
				d(fetchBlockStatuses(extra))
				userId && d(fetchUserRules({ ...extra, userId }))
			},
		],
		blockStatusPollingMs,
	)

	usePolling([(extra) => d(fetchDevices(extra))], devicesPollingMs)
	usePolling([(extra) => d(fetchUsers(extra))], usersPollingMs)

	// NOTE: See BlockStatus comment above
	usePolling(
		[
			(extra) =>
				blockStatusFastPollIds.map((userId) => {
					const args = { ...extra, userId }
					d(fetchUserBlockStatus(args))
					d(fetchUserRules(args))
				}),
		],
		userBlockStatusPollingMs,
		false,
	)

	useEffect(() => {
		if (shouldSetNotTrialEligible) {
			d(setPurchaseSummary({ is_trial_eligible: false }))
		}
	}, [shouldSetNotTrialEligible])

	return null
}

/**
 * Combines standard polling models that we need throughout app so they run
 * when authenticated. This doesn't do _all_ polling, just the models we need
 * everywhere.
 */
const AuthPollingEffects = () => {
	const isAuthenticated = useSelector(isAuthenticated$)

	if (!isAuthenticated) return null

	return <AuthPollingImpl />
}

export default /*@__PURE__*/ memo(AuthPollingEffects)

type BlockStatusPollingData = [
	pollDuration: number | null,
	userPollDuration: number | null,
	fastPollUserIds: string[],
]
/**
 * Calculates the block status polling data. When a user performs an action that can affect block
 * status, components will trigger to speed up polling for the user who changed. To strike a
 * balance between the increase in requests and the complexity of the requests on the server, we
 * only do individual user fast polling if there are fewer than 3 fast separate users in a fast
 * poll state.
 *
 * If there are 3 or more users in a fast poll state at the same time, we increase the polling on
 * the block status fetch for all users, and stop polling for the individual users.
 *
 * In other words the `userBlockStatusPollingUserIds` array is at most 2 users.
 */
const getBlockStatusPollingData = (
	blockStatusExpiryMap: UserFastPollExpiryMap,
	now: number,
): BlockStatusPollingData => {
	const blockStatusPollingUserIds: string[] = []

	for (const userId in blockStatusExpiryMap) {
		const fastPollTs = blockStatusExpiryMap[userId]

		if (now < fastPollTs) {
			// If we're about to add a 3rd user, increase polling for all users and set individual
			// user polling to null.
			if (blockStatusPollingUserIds.length === 2) {
				return [usePolling.FAST, null, blockStatusPollingUserIds] as const
			}

			blockStatusPollingUserIds.push(userId)
		}
	}

	const userBlockStatusPollingMs = blockStatusPollingUserIds.length ? usePolling.FAST : null

	return [usePolling.LONG, userBlockStatusPollingMs, blockStatusPollingUserIds] as const
}
